/**
 * #60 / DEC-365 — the web side of changing a job's pickup site.
 *
 *   GET  /v1/jobs/:id/site-options?q=   the customer's sites, each location's wells, the
 *                                       current site's tanks and the job's captured tank rows
 *   POST /v1/jobs/:id/site              the change (reason required; expectedSite guards a race)
 *   POST /v1/jobs/:id/capture-tanks/:tankRowId/link   re-pick a captured tank's tank
 *
 * All three are CHANGE_JOB_SITE (CHANGE_SITE_ROLES), which the reviewers hold although they
 * cannot read the hauler catalog — so the pickers read site-options, never the catalog.
 */

import { useQuery, type QueryClient } from '@tanstack/react-query';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import type { JobSiteOptionsResponse } from '@lodex-pro/shared/jobs/site-change';

type ApiOpts = ReturnType<typeof useApiOpts>;

/** Statuses in which the site can no longer change (the API's SITE_CHANGE_LOCKED). */
export const SITE_LOCKED_STATUSES: ReadonlySet<string> = new Set([
  'TO_BE_INVOICED',
  'INVOICED',
  'CANCELLED',
  'REJECTED',
]);

/** Whether the job is still inside the site-change window (up to approval, not voided). */
export function siteChangeOpen(statusCode: string, captureVoidedAt: string | null): boolean {
  return !SITE_LOCKED_STATUSES.has(statusCode) && captureVoidedAt === null;
}

export const siteOptionsQueryKey = (jobId: string, q: string): readonly unknown[] => [
  'job-site-options',
  jobId,
  q,
];

export function useSiteOptions(
  apiOpts: ApiOpts,
  jobId: string | null,
  q: string,
  enabled: boolean,
): ReturnType<typeof useQuery<JobSiteOptionsResponse>> {
  return useQuery<JobSiteOptionsResponse>({
    queryKey: siteOptionsQueryKey(jobId ?? '', q),
    enabled: enabled && jobId !== null,
    queryFn: async () => {
      const qs = q.trim() === '' ? '' : `?q=${encodeURIComponent(q.trim())}`;
      const res = await authedFetch(apiOpts, `/v1/jobs/${jobId ?? ''}/site-options${qs}`);
      if (!res.ok) throw new Error(await readSiteChangeError(res));
      return res.json() as Promise<JobSiteOptionsResponse>;
    },
  });
}

const GENERIC_FAILURE = 'Could not change the site. Try again.';

/** Field reasons the API returns, in words a dispatcher reads. */
const FIELD_REASON_TEXT: Readonly<Record<string, string>> = {
  site_not_pickable: "That site can't be picked up from any more. Pick another.",
  well_not_linked_to_location: 'That well is no longer linked to this location.',
  tank_not_at_job_site: "That tank isn't at the job's site.",
};

interface ErrorEnvelope {
  readonly error?: {
    readonly message?: unknown;
    readonly fields?: ReadonlyArray<{ readonly path?: unknown; readonly reason?: unknown }>;
  };
}

/** The API's own sentence for a refusal (or a field reason in words). */
export async function readSiteChangeError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    const field = body.error?.fields?.[0];
    if (typeof field?.reason === 'string') {
      return FIELD_REASON_TEXT[field.reason] ?? field.reason;
    }
    const message = body.error?.message;
    return typeof message === 'string' ? message : GENERIC_FAILURE;
  } catch {
    return GENERIC_FAILURE;
  }
}

/** Everything that shows the job's site, its tanks or its timeline. */
export function invalidateAfterSiteChange(queryClient: QueryClient, jobId: string): void {
  for (const queryKey of [
    ['job-detail', jobId],
    ['job-audit', jobId],
    ['job-site-options', jobId],
    ['jobs'],
    ['inreview'],
  ]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}
